import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Agreement,
  AgreementSearchRequest,
  ApproveAgreementRequest,
  CreateLandownerAgreementRequest,
  DigitalSignatureViewModel,
  PagedResults,
} from '@app/models';
import AgreementViewModel from '@app/models-internal/agreement-view-model';
import CreateLeaseAgreementRequest from '@app/models-internal/create-lease-agreement-request';
import AgreementTypeEnum from '@app/models-internal/enums/agreement-type.enum';
import ExportAgreementRequest from '@app/models-internal/export-agreement-request';
import ExportAgreementResponse from '@app/models-internal/export-agreement-response';
import MapConfiguration from '@app/models-internal/map-configuration';
import AgreementTemplateViewModel from '@app/models/agreement-template-view-model';
import ListingContactViewModel2 from '@app/models/listing-contact-view-model2';
import TerminateAgreementRequest from '@app/models/terminate-agreement-request';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { DateService, LogService, ValueService } from '@oa/shared-core';

@Injectable({
  providedIn: "root",
})
export class AgreementService {
  private readonly _componentName = 'AgreementService';

  /**
   * A subject for broadcasting updates to agreements
   */
  public changed = new Subject<AgreementViewModel>();

  /**
   * A subject for broadcasting new agreements
   */
  public added = new BehaviorSubject<AgreementViewModel>(null);

  constructor(private log: LogService, private http: HttpClient, private valueHelper: ValueService, private dateService: DateService) {}

  /**
   * Terminates an existing agreement
   * @param agreementType
   * @param request
   * @returns
   */
  public terminate(agreementType: AgreementTypeEnum, request: TerminateAgreementRequest) {
    switch (agreementType) {
      case AgreementTypeEnum.LandownerAgreement:
        return this.http.post<AgreementViewModel>('landowner-agreement/terminate', request);

      default:
        throw new Error(` Agreement Type: '${agreementType}' not supported.`);
    }
  }

  /**
   * Exports an agreement to a specified format (typically, a PDF)
   * @param request
   * @param agreementType
   * @returns
   */
  public export(request: ExportAgreementRequest, agreementType: AgreementTypeEnum) {
    if (!request) {
      return throwError('Invalid ExportAgreementRequest');
    }

    switch (agreementType) {
      case AgreementTypeEnum.LandownerAgreement:
        return this.http.post<ExportAgreementResponse>('landowner-agreement/export', request);

      default:
        throw new Error(` Agreement Type: '${agreementType}' not supported.`);
    }
  }

  /**
   * Searches for existing agreements
   * @param request
   * @returns
   */
  public search(request: AgreementSearchRequest) {
    const url = 'agreement/search';

    return this.http.post<PagedResults>(url, request);
  }

  public createNewAgreement(request: any) {}

  public createNewLandownerAgreement(request: CreateLandownerAgreementRequest) {
    if (!request || !request.templateName) {
      return throwError('Invalid CreateLandownerAgreementRequest');
    }

    return this.http.post<AgreementViewModel>('landowner-agreement', request);
  }

  /**
   * Creates a new lease agreement
   */
  public createNewLeaseAgreement(request: CreateLeaseAgreementRequest) {
    if (!request || !request.templateName) {
      return throwError('Invalid CreateLeaseAgreementRequest');
    }

    return this.http.post<AgreementViewModel>('lease-agreement', request);
  }

  /**
   * Updates a single data item for an Agreement.
   * @param id - Secure Alternate ID value (GUID/UUID)
   * @param path
   * @param value
   * @returns
   */
  public patch<K extends keyof Agreement>(id: string, path: K, value: Agreement[K]): Observable<{}> {
    return this.patchJson(id, [
      {
        path,
        value,
        op: 'replace',
      },
    ]);
  }

  /**
   * Updates a subset of data items for an Agreement.
   * @param id - Secure Alternate ID value (GUID/UUID)
   * @param patch
   * @returns
   */
  public patchJson<K extends keyof Agreement>(id: string, patch: AgreementPatch<K>[]): Observable<Agreement> {
    return this.http.patch<Agreement>(
      `Agreement/${id}`,
      patch.map((p) => ({ ...p, path: `/${p.path}` }))
    );
  }

  /**
   * Deletes an agreement
   * @param a
   */
  public deleteAgreement(a: AgreementViewModel) {
    return this.http.delete(`landowner-agreement/${a.secureAlternateId}`);
  }

  // Renders the agreement content
  public renderAgreement(agreement: AgreementViewModel) {
    if (!agreement) {
      return throwError('Invalid AgreementViewModel');
    }

    const key = agreement.type.toLowerCase();

    switch (key) {
      case 'landowneragreement':
        return this.http.get<AgreementViewModel>(`landowner-agreement/${agreement.secureAlternateId}/template/render`);

      default:
        return this.http.get<AgreementViewModel>(`lease-agreement/${agreement.secureAlternateId}/template/render`);
    }
  }

  /**
   * Approve an Agreement, returns an updated AgreementViewModel
   * @param request
   * @returns
   */
  public approveAgreement(request: ApproveAgreementRequest) {
    if (!request) {
      return throwError('Invalid ApproveAgreementRequest');
    }

    const key = request.type.toLowerCase();

    switch (key) {
      case 'landowneragreement':
        return this.http.post<AgreementViewModel>('landowner-agreement/approve', request);

      default:
        return throwError(` Agreement Type: '${request.type}' not supported.`);
    }
  }

  /**
   * Gets a single agreement view model
   * @param id
   * @returns
   */
  public getAgreement(id: string) {
    return this.http.get<AgreementViewModel>(`agreement/${id}`);
  }

  public getTemplates(agreementType: string) {
    if (!agreementType) {
      throw new Error('Missing Argument: Agreement Type');
    }

    const key = agreementType.toLowerCase();

    switch (key) {
      case 'landowneragreement':
        return this.http.get<AgreementTemplateViewModel[]>('landowner-agreement/template');

      default:
        throw new Error(` Agreement Type: '${agreementType}' not supported.`);
    }
  }

  /**
   * Gets the agreement's map configuraton.  If nullish, an empty map configuration will be created.
   * @param agreement
   * @returns
   */
  public getAgreemmentMapConfiguration(agreement: AgreementViewModel): MapConfiguration {
    if (agreement.mapExtent) {
      return JSON.parse(agreement.mapExtent) as MapConfiguration;
    } else {
      console.log(
        `${this._componentName}.getAgreemmentMapConfiguration - Map Configuration object created for agreement: ${agreement.secureAlternateId}`
      );

      return {
        uuid: this.valueHelper.generateUUID(),
        name: 'Agreement Map Configuration',
      } as MapConfiguration;
    }
  }

  /**
   * Gets the counter signature for an agreement
   * @param id
   * @returns
   */
  public getDigitalCounterSignature(id: string) {
    return this.http.get<DigitalSignatureViewModel>(`agreement/${id}/csig`);
  }

  public getDigitalSignature(id: string) {
    return this.http.get<DigitalSignatureViewModel>(`agreement/${id}/sig`);
  }

  /**
   * Builds a default Landowner Agreement Name from a contact
   * @param contact
   * @returns
   */
  public buildLandownerAgreementName(contact: ListingContactViewModel2): string {
    if (contact && contact.fullName) {
      return `${contact.fullName} Outdoor Access Licensing Agreement ${this.dateService.formatDate(new Date())}`;
    }

    return null;
  }
}

export type AgreementPatch<K extends string & keyof Agreement> = { path: K; value: Agreement[K]; op: 'replace' };
